import type { Job } from '../../../app/types/index'
import { getStateCode, getStateName } from '~~/shared/config/states'

const COUNTRY_NAMES = ['usa', 'united states', 'united states of america', 'us', 'canada', 'uk', 'united kingdom']

/**
 * Normalize a location string - convert state codes to full state names
 * e.g., "CA" -> "California", "TX" -> "Texas"
 */
function normalizeLocationName(location: string): string {
  const trimmed = location.trim()
  // Check if it's a state code (like "CA" -> "California")
  const stateName = getStateName(trimmed)
  if (stateName) {
    return stateName
  }
  return trimmed
}

function getLocationRequirementType(location: string): 'Country' | 'State' | 'AdministrativeArea' {
  const normalized = location.toLowerCase().trim()
  if (COUNTRY_NAMES.includes(normalized)) {
    return 'Country'
  }
  // Check if it's a full state name
  if (getStateCode(location)) {
    return 'State'
  }
  // Check if it's a state code (like "CA")
  if (getStateName(location.trim())) {
    return 'State'
  }
  return 'AdministrativeArea'
}

interface JobPostingSchema {
  '@context': string
  '@type': string
  '@id': string
  'title': string
  'description': string
  'datePosted': string
  'validThrough': string
  'employmentType'?: string | string[]
  'hiringOrganization': {
    '@type': string
    'name': string
    'logo': string
  }
  'jobLocation': {
    '@type': string
    'address': {
      '@type': string
      'addressLocality': string
      'addressRegion': string
      'addressCountry': string
      'postalCode'?: string
    }
    'geo'?: {
      '@type': string
      'latitude': number
      'longitude': number
    }
  }
  'baseSalary'?: {
    '@type': string
    'currency': string
    'value': {
      '@type': string
      'value'?: number
      'minValue'?: number
      'maxValue'?: number
      'medianValue'?: number
      'unitText': string
    }
  }
  'identifier': {
    '@type': string
    'name': string
    'value': string
  }
  'industry'?: string
  'url': string
  'impressionUrl'?: string
  'relevantOccupation'?: {
    '@type': string
    'name': string
    'occupationalCategory': {
      '@type': string
      'inCodeSet': {
        '@type': string
        'name': string
        'dateModified': string
        'url': string
      }
      'codeValue': string
      'name': string
      'url': string
    }
  }
  'jobLocationType'?: string
  'workHours'?: string
  'jobStartDate'?: string
  'jobImmediateStart'?: boolean
  'directApply'?: boolean
  'incentiveCompensation'?: string
  'jobBenefits'?: string | string[]
  'educationRequirements'?: string
  'experienceRequirements'?: string
  'skills'?: string | string[]
  'responsibilities'?: string | string[]
  'qualifications'?: string | string[]
  'occupationalCategory'?: string
  'totalJobOpenings'?: number
  'physicalRequirement'?: string
  'sensoryRequirement'?: string
  'securityClearanceRequirement'?: string
  'eligibilityToWorkRequirement'?: string
  'specialCommitments'?: string | string[]
  'employerOverview'?: string
  'applicationContact'?: {
    '@type': string
    'name'?: string
    'email'?: string
    'telephone'?: string
    'url'?: string
  }
  'applicantLocationRequirements'?: Array<{
    '@type': 'Country' | 'State' | 'AdministrativeArea'
    'name': string
  }>
}
export function generateJobPostingSchema(job: Job, canonicalUrl?: string): JobPostingSchema {
  const datePosted = new Date(job.date)
  const validThrough = new Date(datePosted)
  validThrough.setDate(validThrough.getDate() + 30)
  const jobUrl = canonicalUrl
    ? `https://employers.io${canonicalUrl}`
    : `https://employers.io/viewJob/${job.j_id}`
  const jobId = canonicalUrl
    ? `https://employers.io${canonicalUrl}#jobposting`
    : `https://employers.io/viewJob/${job.j_id}#jobposting`
  const aiFields = job.ai_analysis?.schema_org_fields
  const schema: JobPostingSchema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    '@id': jobId,
    'title': job.title,
    'description': job.description,
    'url': jobUrl,
    'datePosted': job.date,
    'validThrough': aiFields?.validThrough || validThrough.toISOString(),
    'hiringOrganization': {
      '@type': 'Organization',
      'name': job.company,
      'logo': job.logo
    },
    'jobLocation': {
      '@type': 'Place',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': job.location.city,
        'addressRegion': job.location.state,
        'addressCountry': job.location.country,
        'postalCode': job.location.zip
      },
      // Add geo coordinates if available (from organic_jobs latlon field)
      ...(job.latlon?.lat && job.latlon?.lon ? {
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': job.latlon.lat,
          'longitude': job.latlon.lon
        }
      } : {})
    },
    'identifier': {
      '@type': 'PropertyValue',
      'name': job.company || 'Unknown',
      'value': job.j_id
    },
  }
  if (aiFields?.employmentType && aiFields.employmentType.length > 0) {
    schema.employmentType = aiFields.employmentType.length === 1
      ? aiFields.employmentType[0]
      : aiFields.employmentType
  } else {
    schema.employmentType = 'FULL_TIME'
  }
  if (aiFields?.industry) {
    schema.industry = aiFields.industry
  } else if (job.category) {
    schema.industry = job.category
  }
  const aiSalary = job.ai_analysis?.compensation_and_benefits?.salary
  if (aiFields?.baseSalary && (aiFields.baseSalary.minValue || aiFields.baseSalary.maxValue || aiFields.baseSalary.value)) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      'currency': aiFields.salaryCurrency || 'USD',
      'value': {
        '@type': 'QuantitativeValue',
        'value': aiFields.baseSalary.value || undefined,
        'minValue': aiFields.baseSalary.minValue || undefined,
        'maxValue': aiFields.baseSalary.maxValue || undefined,
        'unitText': aiFields.baseSalary.unitText || 'YEAR'
      }
    }
  } else if (aiSalary && (aiSalary.min_value || aiSalary.max_value || aiSalary.value)) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      'currency': aiSalary.currency || 'USD',
      'value': {
        '@type': 'QuantitativeValue',
        'value': aiSalary.value || undefined,
        'minValue': aiSalary.min_value || undefined,
        'maxValue': aiSalary.max_value || undefined,
        'unitText': aiSalary.pay_period || 'YEAR'
      }
    }
  } else if (job.estimated_salary?.adjusted_salary) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      'currency': job.estimated_salary.currency || 'USD',
      'value': {
        '@type': 'QuantitativeValue',
        'minValue': Math.round(job.estimated_salary.adjusted_salary.P25),
        'maxValue': Math.round(job.estimated_salary.adjusted_salary.P75),
        'medianValue': Math.round(job.estimated_salary.adjusted_salary.P50),
        'unitText': job.estimated_salary.pay_period === 'annual' ? 'YEAR' : 'HOUR'
      }
    }
  }
  const socCode = aiFields?.occupationalCategory || job.soc_code || job.estimated_salary?.soc_code
  const jobTitle = job.estimated_salary?.job_title || job.title
  if (socCode) {
    schema.relevantOccupation = {
      '@type': 'Occupation',
      'name': jobTitle,
      'occupationalCategory': {
        '@type': 'CategoryCode',
        'inCodeSet': {
          '@type': 'CategoryCodeSet',
          'name': 'O*Net-SOC',
          'dateModified': '2019',
          'url': 'https://www.onetonline.org/'
        },
        'codeValue': socCode,
        'name': jobTitle,
        'url': `https://www.onetonline.org/link/summary/${socCode}`
      }
    }
  }
  if (aiFields) {
    if (aiFields.jobLocationType) schema.jobLocationType = aiFields.jobLocationType
    if (aiFields.workHours) schema.workHours = aiFields.workHours
    if (aiFields.jobStartDate) schema.jobStartDate = aiFields.jobStartDate
    if (aiFields.jobImmediateStart !== null && aiFields.jobImmediateStart !== undefined) {
      schema.jobImmediateStart = aiFields.jobImmediateStart
    }
    if (aiFields.directApply !== null && aiFields.directApply !== undefined) {
      schema.directApply = aiFields.directApply
    }
    if (aiFields.incentiveCompensation) {
      schema.incentiveCompensation = aiFields.incentiveCompensation
    } else {
      const bonusDetails = job.ai_analysis?.compensation_and_benefits?.bonus_compensation
      const commissionDetails = job.ai_analysis?.compensation_and_benefits?.commission_details
      const incentiveParts = []
      if (bonusDetails) incentiveParts.push(bonusDetails)
      if (commissionDetails) incentiveParts.push(commissionDetails)
      if (incentiveParts.length > 0) {
        schema.incentiveCompensation = incentiveParts.join('. ')
      }
    }
    if (aiFields.jobBenefits && aiFields.jobBenefits.length > 0) {
      schema.jobBenefits = aiFields.jobBenefits.length === 1
        ? aiFields.jobBenefits[0]
        : aiFields.jobBenefits
    } else if (job.job_benefits && job.job_benefits.length > 0) {
      // Fallback to organic_jobs job_benefits
      schema.jobBenefits = job.job_benefits.length === 1
        ? job.job_benefits[0]
        : job.job_benefits
    }
    if (aiFields.educationRequirements) schema.educationRequirements = aiFields.educationRequirements
    if (aiFields.experienceRequirements) {
      schema.experienceRequirements = aiFields.experienceRequirements
    } else if (job.experience_requirements) {
      // Fallback to organic_jobs experience_requirements
      schema.experienceRequirements = job.experience_requirements
    }
    if (aiFields.skills && aiFields.skills.length > 0) {
      schema.skills = aiFields.skills.length === 1
        ? aiFields.skills[0]
        : aiFields.skills
    }
    if (aiFields.responsibilities && aiFields.responsibilities.length > 0) {
      schema.responsibilities = aiFields.responsibilities.length === 1
        ? aiFields.responsibilities[0]
        : aiFields.responsibilities
    }
    if (aiFields.qualifications && aiFields.qualifications.length > 0) {
      schema.qualifications = aiFields.qualifications.length === 1
        ? aiFields.qualifications[0]
        : aiFields.qualifications
    }
    if (aiFields.totalJobOpenings) schema.totalJobOpenings = aiFields.totalJobOpenings
    if (aiFields.physicalRequirement) schema.physicalRequirement = aiFields.physicalRequirement
    if (aiFields.sensoryRequirement) schema.sensoryRequirement = aiFields.sensoryRequirement
    if (aiFields.securityClearanceRequirement) schema.securityClearanceRequirement = aiFields.securityClearanceRequirement
    if (aiFields.eligibilityToWorkRequirement) schema.eligibilityToWorkRequirement = aiFields.eligibilityToWorkRequirement
    if (aiFields.specialCommitments && aiFields.specialCommitments.length > 0) {
      schema.specialCommitments = aiFields.specialCommitments.length === 1
        ? aiFields.specialCommitments[0]
        : aiFields.specialCommitments
    }
    if (aiFields.employerOverview) schema.employerOverview = aiFields.employerOverview
    if (aiFields.applicationContact && (
      aiFields.applicationContact.name
      || aiFields.applicationContact.email
      || aiFields.applicationContact.phone
      || aiFields.applicationContact.url
    )) {
      schema.applicationContact = {
        '@type': 'ContactPoint',
        'name': aiFields.applicationContact.name || undefined,
        'email': aiFields.applicationContact.email || undefined,
        'telephone': aiFields.applicationContact.phone || undefined,
        'url': aiFields.applicationContact.url || undefined
      }
    }
    if (aiFields.applicantLocationRequirements && aiFields.applicantLocationRequirements.length > 0) {
      schema.applicantLocationRequirements = aiFields.applicantLocationRequirements.map(location => ({
        '@type': getLocationRequirementType(location),
        'name': normalizeLocationName(location)
      }))
    }
  }
  // Default applicantLocationRequirements to country level if not set
  if (!schema.applicantLocationRequirements) {
    const country = job.location?.country || 'United States'
    schema.applicantLocationRequirements = [{
      '@type': 'Country',
      'name': country
    }]
  }
  // Fallback for organic_jobs data when no AI analysis
  if (!schema.experienceRequirements && job.experience_requirements) {
    schema.experienceRequirements = job.experience_requirements
  }
  if (!schema.jobBenefits && job.job_benefits && job.job_benefits.length > 0) {
    schema.jobBenefits = job.job_benefits.length === 1
      ? job.job_benefits[0]
      : job.job_benefits
  }
  return schema
}
